import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import appApi from '../../../../../api/app-api';
import { DepositPaymentType } from '../../../../../api/types/accounting/deposit-payment-type';
import {
  PendingDepositPaymentEntity,
  PendingDepositPaymentSource,
} from '../../../../../api/types/accounting/pending-deposit-payment-entity';
import { ChartOfAccountDetailsEntity } from '../../../../../api/types/settings/accounting/chart-of-accounts/chart-of-account-details.type';
import { AccountTypeEnum } from '../../../../../api/types/settings/accounting/chart-of-accounts/chart-of-accounts.type';
import { WarningDialog } from '../../../../../component/ui/warning-dialog/warning-dialog';
import { formatDate } from '../../../../../utils/date-format';
import { AddPaymentsToExistingDepositDialog } from './components/add-payments-to-existing-deposit-dialog';
import { WaitingForDepositFilter } from './components/waiting-for-deposit-filter';
import { createAccountingDepositOperation } from './operations/create-accounting-deposit-operation';
import { getPendingDepositPaymentsOperation } from './operations/get-pending-deposit-payments-operation';
import {
  PendingDepositPaymentsFilterType,
  defaultPendingDepositPaymentsFilter,
} from './waiting-for-deposit-filter.type';
import { AccountingDepositPaymentDetailsEntity } from '../../../../../api/types/accounting/accounting-deposit-payment-details-entity';

type PendingRowWithKey = PendingDepositPaymentEntity & { __key: string };

const buildRowKey = (row: PendingDepositPaymentEntity, index: number): string =>
  [
    row.source,
    row.userPaymentId ?? '',
    row.userPaymentCheckDetailsId ?? '',
    row.crmPaymentId ?? '',
    row.crmCheckId ?? '',
    index,
  ].join('|');

const sourceLabel = (source: PendingDepositPaymentSource): string => {
  switch (source) {
    case 'user_cash':
      return 'מזומן - סטודנט';
    case 'crm_cash':
      return 'מזומן - CRM';
    case 'user_check':
      return 'המחאה - סטודנט';
    case 'crm_check':
      return 'המחאה - CRM';
    default:
      return source;
  }
};

const sourceSeverity = (
  source: PendingDepositPaymentSource,
): 'success' | 'info' | 'warning' | 'danger' => {
  switch (source) {
    case 'user_cash':
      return 'success';
    case 'crm_cash':
      return 'info';
    case 'user_check':
      return 'warning';
    case 'crm_check':
      return 'danger';
    default:
      return 'info';
  }
};

const paymentTypeLabel = (paymentType: DepositPaymentType): string => {
  switch (paymentType) {
    case DepositPaymentType.CASH:
      return 'מזומן';
    case DepositPaymentType.CHECK:
      return 'המחאה';
    default:
      return paymentType;
  }
};

const toPaymentItem = (row: PendingDepositPaymentEntity) => ({
  paymentType: row.paymentType,
  userPaymentId: row.userPaymentId,
  userPaymentCheckDetailsId: row.userPaymentCheckDetailsId,
  crmPaymentId: row.crmPaymentId,
  crmCheckId: row.crmCheckId,
});

export const WaitingForDepositTab = () => {
  const [data, setData] = useState<PendingDepositPaymentEntity[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [filters, setFilters] = useState<PendingDepositPaymentsFilterType>(
    defaultPendingDepositPaymentsFilter(),
  );

  const [selectedRows, setSelectedRows] = useState<PendingRowWithKey[]>([]);

  const [bankAccounts, setBankAccounts] = useState<
    ChartOfAccountDetailsEntity[]
  >([]);
  const [bankAccountId, setBankAccountId] = useState<number | null>(null);
  const [depositDate, setDepositDate] = useState<Date>(new Date());
  const [notes, setNotes] = useState<string>('');

  const [showWarningDialog, setShowWarningDialog] = useState<boolean>(false);
  const [creatingDeposit, setCreatingDeposit] = useState<boolean>(false);

  const [showAddToExistingDialog, setShowAddToExistingDialog] =
    useState<boolean>(false);

  const rowsWithKeys: PendingRowWithKey[] = useMemo(
    () => data.map((row, idx) => ({ ...row, __key: buildRowKey(row, idx) })),
    [data],
  );

  const runSearch = async (nextFilters: PendingDepositPaymentsFilterType) => {
    setSelectedRows([]);
    await getPendingDepositPaymentsOperation(nextFilters, setData, setLoading);
  };

  const fetchBankAccounts = async () => {
    try {
      const res = await appApi.getChartOfAccountsList();
      const list = res.data.body ?? [];
      setBankAccounts(
        list.filter(
          (acc) => acc.accountType === AccountTypeEnum.BANK && acc.isActive,
        ),
      );
    } catch (e) {
      // silent - the bank account select shows empty + the operation guards against missing bankAccountId
    }
  };

  useEffect(() => {
    runSearch(filters);
    fetchBankAccounts();
  }, []);

  const handleSearch = async () => {
    await runSearch(filters);
  };

  const handleClear = () => {
    setFilters(defaultPendingDepositPaymentsFilter());
  };

  const totalAmount = data.reduce(
    (sum, row) => sum + (typeof row.amount === 'number' ? row.amount : 0),
    0,
  );

  const selectedTotal = selectedRows.reduce(
    (sum, row) => sum + (typeof row.amount === 'number' ? row.amount : 0),
    0,
  );

  const hasSelection = selectedRows.length > 0;

  const bankAccountOptions = bankAccounts.map((acc) => ({
    label: `${acc.accountName}${acc.accountCode ? ` (${acc.accountCode})` : ''}`,
    value: acc.id,
  }));

  const canOpenWarning =
    hasSelection &&
    bankAccountId !== null &&
    bankAccountId > 0 &&
    !!depositDate &&
    selectedTotal > 0;

  const onCreateDepositClick = () => {
    if (!canOpenWarning) return;
    setShowWarningDialog(true);
  };

  const onConfirmCreateDeposit = async () => {
    if (bankAccountId === null) return;
    await createAccountingDepositOperation(
      {
        bankAccountId,
        depositDate,
        totalAmount: selectedTotal,
        notes: notes,
        payments: selectedRows.map(toPaymentItem),
      },
      setCreatingDeposit,
      async () => {
        setShowWarningDialog(false);
        setSelectedRows([]);
        setNotes('');
        setBankAccountId(null);
        setDepositDate(new Date());
        await runSearch(filters);
      },
    );
  };

  const styles = {
    actionBar: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '10px',
    },
    actionTopRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '16px',
      fontSize: '14px',
      color: '#4b5563',
      flexWrap: 'wrap' as const,
    },
    actionFormRow: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: '10px',
      flexWrap: 'wrap' as const,
      padding: '10px',
      borderRadius: '6px',
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
    },
    field: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '4px',
    },
    fieldLabel: {
      fontSize: '0.8rem',
      color: '#475569',
    },
    notesField: {
      flex: 1,
      minWidth: '180px',
    },
    selectedSummary: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '6px 10px',
      borderRadius: '6px',
      background: '#ecfdf5',
      border: '1px solid #a7f3d0',
      color: '#065f46',
      fontWeight: 600 as const,
    },
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        padding: '12px',
        direction: 'rtl',
      }}
    >
      <WaitingForDepositFilter
        filters={filters}
        loading={loading}
        onChange={setFilters}
        onSearch={handleSearch}
        onClear={handleClear}
      />

      <div
        style={{
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
          padding: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        <div style={styles.actionBar}>
          <div style={styles.actionTopRow}>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <span>סה״כ רשומות: {data.length.toLocaleString()}</span>
              <span>סה״כ סכום: {totalAmount.toFixed(2)}</span>
              {hasSelection && (
                <span style={styles.selectedSummary}>
                  נבחרו: {selectedRows.length} | סכום:{' '}
                  {selectedTotal.toFixed(2)}
                </span>
              )}
            </div>

            {hasSelection && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  icon="pi pi-arrow-right-arrow-left"
                  label="הוספה להפקדה קיימת"
                  onClick={() => setShowAddToExistingDialog(true)}
                  disabled={!hasSelection || creatingDeposit}
                  severity="info"
                />
                <Button
                  icon="pi pi-plus"
                  label="יצירת הפקדה"
                  onClick={onCreateDepositClick}
                  disabled={!canOpenWarning || creatingDeposit}
                  tooltip={
                    !bankAccountId
                      ? 'יש לבחור חשבון בנק'
                      : !depositDate
                        ? 'יש לבחור תאריך הפקדה'
                        : undefined
                  }
                  tooltipOptions={{ position: 'top' }}
                  severity="success"
                />
              </div>
            )}
          </div>

          {hasSelection && (
            <div style={styles.actionFormRow}>
              <div style={styles.field}>
                <label style={styles.fieldLabel}>חשבון בנק</label>
                <Dropdown
                  value={bankAccountId}
                  options={bankAccountOptions}
                  onChange={(e) => setBankAccountId(e.value ?? null)}
                  placeholder="בחר חשבון בנק"
                  filter
                  emptyMessage="לא נמצאו חשבונות בנק"
                  emptyFilterMessage="לא נמצאו חשבונות בנק"
                  style={{ minWidth: '240px' }}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.fieldLabel}>תאריך הפקדה</label>
                <Calendar
                  value={depositDate}
                  onChange={(e) =>
                    setDepositDate((e.value as Date) ?? new Date())
                  }
                  dateFormat="dd/mm/yy"
                  showIcon
                  style={{ width: '180px', direction: 'ltr' }}
                  inputStyle={{ direction: 'rtl', width: '100%' }}
                />
              </div>

              <div style={{ ...styles.field, ...styles.notesField }}>
                <label style={styles.fieldLabel}>הערות (אופציונלי)</label>
                <InputText
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  maxLength={2000}
                  placeholder="הערות חופשיות..."
                />
              </div>
            </div>
          )}
        </div>

        <DataTable
          value={rowsWithKeys}
          rowHover
          loading={loading}
          paginator
          rows={20}
          rowsPerPageOptions={[10, 20, 50, 100]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          emptyMessage={
            <div style={{ textAlign: 'right' }}>לא נמצאו תשלומים ממתינים</div>
          }
          tableStyle={{ direction: 'rtl' }}
          dataKey="__key"
          selectionMode="multiple"
          selection={selectedRows}
          onSelectionChange={(e) =>
            setSelectedRows(e.value as PendingRowWithKey[])
          }
        >
          <Column
            selectionMode="multiple"
            headerStyle={{ width: '3rem' }}
            style={{ width: '3rem', textAlign: 'center' }}
          />
          <Column
            field="source"
            header="מקור"
            style={{ textAlign: 'right', width: '160px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: PendingDepositPaymentEntity) => (
              <Tag
                value={sourceLabel(row.source)}
                severity={sourceSeverity(row.source)}
              />
            )}
          />
          <Column
            field="paymentType"
            header="סוג תשלום"
            style={{ textAlign: 'right', width: '120px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: PendingDepositPaymentEntity) =>
              paymentTypeLabel(row.paymentType)
            }
          />
          <Column
            header="שלוחות"
            style={{ textAlign: 'right', width: '180px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: PendingDepositPaymentEntity) =>
              row.branches && row.branches.length > 0
                ? row.branches.map((b) => b.branchName).join(', ')
                : '—'
            }
          />
          <Column
            field="amount"
            header="סכום"
            style={{ textAlign: 'right', width: '120px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: PendingDepositPaymentEntity) =>
              typeof row.amount === 'number' ? row.amount.toFixed(2) : 'חסר'
            }
          />
          <Column
            field="accountingDocument.docNumber"
            header="מספר מסמך"
            style={{ textAlign: 'right', width: '140px' }}
            headerStyle={{ textAlign: 'right' }}
            /*body={(row: PendingDepositPaymentEntity) =>
              row.accountingDocumentCreatedAt
                ? formatDate(row.accountingDocumentCreatedAt as Date)
                : 'חסר'
            }*/
          />

          <Column
            field="effectiveDate"
            header="תאריך הפקה"
            style={{ textAlign: 'right', width: '140px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: PendingDepositPaymentEntity) =>
              row.accountingDocumentCreatedAt
                ? formatDate(row.accountingDocumentCreatedAt as Date)
                : 'חסר'
            }
          />

          <Column
            field="effectiveDate"
            header="תאריך ערך"
            style={{ textAlign: 'right', width: '140px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: PendingDepositPaymentEntity) =>
              row.dueDate ? formatDate(row.dueDate as Date) : 'חסר'
            }
          />
          <Column
            field="bankNumber"
            header="בנק"
            style={{ textAlign: 'right', width: '90px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: PendingDepositPaymentEntity) =>
              row.bankNumber?.trim() ? row.bankNumber : '—'
            }
          />
          <Column
            field="branchNumber"
            header="סניף"
            style={{ textAlign: 'right', width: '90px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: PendingDepositPaymentEntity) =>
              row.branchNumber?.trim() ? row.branchNumber : '—'
            }
          />
          <Column
            field="accountNumber"
            header="מס׳ חשבון"
            style={{ textAlign: 'right', width: '130px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: PendingDepositPaymentEntity) =>
              row.accountNumber?.trim() ? row.accountNumber : '—'
            }
          />
          <Column
            field="referenceId"
            header="אסמכתא"
            style={{ textAlign: 'right', width: '130px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: PendingDepositPaymentEntity) =>
              row.referenceId?.trim() ? row.referenceId : '—'
            }
          />
          <Column
            field="createdByName"
            header="נוצר על ידי"
            style={{ textAlign: 'right', width: '160px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: PendingDepositPaymentEntity) =>
              row.createdByName?.trim() ? row.createdByName : 'חסר'
            }
          />

          <Column
            header="קובץ"
            style={{ textAlign: 'right', width: '100px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: PendingDepositPaymentEntity) =>
              row.accountingDocument?.fileUrl ? (
                <a
                  href={row.accountingDocument.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#2563eb',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <i className="pi pi-external-link" />
                  פתח
                </a>
              ) : (
                <span style={{ color: '#9ca3af' }}>חסר</span>
              )
            }
          />
        </DataTable>
      </div>

      <WarningDialog
        header="אישור יצירת הפקדה"
        isVisible={showWarningDialog}
        onHide={() => {
          if (!creatingDeposit) setShowWarningDialog(false);
        }}
        addIcon
        content={`האם אתה בטוח שברצונך ליצור הפקדה עבור ${selectedRows.length} תשלומים?`}
        dangerousContent={`סכום כולל: ${selectedTotal.toFixed(2)} ש״ח | תאריך: ${depositDate ? formatDate(depositDate) : ''}`}
        smallLetters={
          bankAccountOptions.find((o) => o.value === bankAccountId)?.label
            ? `חשבון בנק: ${
                bankAccountOptions.find((o) => o.value === bankAccountId)?.label
              }`
            : undefined
        }
        confirmLabel="צור הפקדה"
        cancelLabel="ביטול"
        confirmLoading={creatingDeposit}
        onConfirm={onConfirmCreateDeposit}
      />

      <AddPaymentsToExistingDepositDialog
        visible={showAddToExistingDialog}
        paymentsCount={selectedRows.length}
        totalAmount={selectedTotal}
        payments={selectedRows.map(toPaymentItem)}
        onHide={() => setShowAddToExistingDialog(false)}
        onSuccess={async () => {
          setSelectedRows([]);
          await runSearch(filters);
        }}
      />
    </div>
  );
};
